/**
 * <mellow-otp>: one-time-code input made of single-character boxes.
 *
 * Exposes the combined code as .value and fires `input` (CustomEvent with the code as
 * detail) so wire:model on the element works. The individual boxes' own events are
 * stopped, otherwise Livewire would read a single character.
 */
export class MellowOtp extends HTMLElement {
    connectedCallback() {
        if (this._ready) return;
        this._ready = true;

        this.addEventListener('input', this.onInput, true);
        this.addEventListener('change', this.stop, true);
        this.addEventListener('keydown', this.onKeydown);
        this.addEventListener('paste', this.onPaste);
        this.addEventListener('focusin', (event) => event.target.select?.());
    }

    get inputs() {
        return [...this.querySelectorAll('.mellow-otp-input')];
    }

    get length() {
        return Number(this.getAttribute('length') || this.inputs.length);
    }

    get value() {
        return this.inputs.map((input) => input.value).join('');
    }

    set value(value) {
        const chars = [...String(value ?? '')];
        this.inputs.forEach((input, index) => (input.value = chars[index] ?? ''));
        this.syncHidden();
    }

    stop = (event) => {
        if (event.target !== this) event.stopPropagation();
    };

    onInput = (event) => {
        if (event.target === this) return;
        event.stopPropagation();

        const input = event.target;
        const index = this.inputs.indexOf(input);
        const chars = this.filter(input.value);

        // Autofill (one-time-code) or fast typing can put several characters into one box.
        if (chars.length > 1) {
            this.fill(chars, index);
        } else {
            input.value = chars;
            if (chars) this.inputs[index + 1]?.focus();
        }

        this.commit();
    };

    onKeydown = (event) => {
        const input = event.target;
        const index = this.inputs.indexOf(input);
        if (index === -1) return;

        const rtl = getComputedStyle(this).direction === 'rtl';
        const previous = this.inputs[index - 1];
        const next = this.inputs[index + 1];

        if (event.key === 'Backspace' && !input.value && previous) {
            event.preventDefault();
            previous.value = '';
            previous.focus();
            this.commit();
        } else if (event.key === (rtl ? 'ArrowRight' : 'ArrowLeft') && previous) {
            event.preventDefault();
            previous.focus();
        } else if (event.key === (rtl ? 'ArrowLeft' : 'ArrowRight') && next) {
            event.preventDefault();
            next.focus();
        }
    };

    onPaste = (event) => {
        const text = event.clipboardData?.getData('text') ?? '';
        const index = Math.max(0, this.inputs.indexOf(event.target));
        event.preventDefault();
        this.fill(this.filter(text), index);
        this.commit();
    };

    fill(chars, start) {
        const inputs = this.inputs;
        [...chars].slice(0, inputs.length - start).forEach((char, offset) => (inputs[start + offset].value = char));
        inputs[Math.min(start + chars.length, inputs.length - 1)].focus();
    }

    filter(value) {
        const pattern = this.getAttribute('mode') === 'numeric' ? /\D/g : /[^\p{L}\p{N}]/gu;
        return String(value).replace(pattern, '');
    }

    commit() {
        this.syncHidden();
        this.dispatchEvent(new CustomEvent('input', { bubbles: true, detail: this.value }));
        this.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: this.value }));

        if (this.value.length === this.length) {
            this.dispatchEvent(new CustomEvent('mellow:complete', { bubbles: true, detail: this.value }));
            if (this.getAttribute('submit') === 'auto') this.closest('form')?.requestSubmit();
        }
    }

    syncHidden() {
        const hidden = this.querySelector('[data-mellow-otp-value]');
        if (hidden) hidden.value = this.value;
    }
}
